import type { Relationship, PrimaryEntityType } from '@/lib/types';

type EventInvolvementEntityType = PrimaryEntityType;
type EventMembershipField = 'characterIds' | 'locationIds' | 'organizationIds';

type EventMembershipData = Partial<Record<EventMembershipField, unknown>>;

type EventInvolvementConfig = {
  entityType: EventInvolvementEntityType;
  field: EventMembershipField;
  forwardDescription: string;
  reverseDescription: string;
};

const EVENT_INVOLVEMENT_CONFIGS: EventInvolvementConfig[] = [
  {
    entityType: 'characters',
    field: 'characterIds',
    forwardDescription: 'involves',
    reverseDescription: 'is involved in',
  },
  {
    entityType: 'organizations',
    field: 'organizationIds',
    forwardDescription: 'involves',
    reverseDescription: 'is involved in',
  },
  {
    entityType: 'locations',
    field: 'locationIds',
    forwardDescription: 'takes place at',
    reverseDescription: 'is the location of',
  },
];

const EVENT_MEMBERSHIP_FIELDS = EVENT_INVOLVEMENT_CONFIGS.map((config) => config.field);

export function hasEventInvolvementMembershipFields(data: Record<string, unknown>) {
  return EVENT_MEMBERSHIP_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(data, field));
}

function toSafeRelationshipIdSegment(value: string) {
  return value.replace(/[^A-Za-z0-9_-]/g, '_');
}

export function getEventInvolvementRelationshipId(
  eventId: string,
  entityType: EventInvolvementEntityType,
  entityId: string,
) {
  return [
    'event_involvement',
    toSafeRelationshipIdSegment(eventId),
    entityType,
    toSafeRelationshipIdSegment(entityId),
  ].join('__');
}

function getMembershipIds(data: EventMembershipData | null | undefined, field: EventMembershipField) {
  const value = data?.[field];
  if (!Array.isArray(value)) return [];

  return Array.from(new Set(value.filter((id): id is string => typeof id === 'string' && id.length > 0)));
}

export function getEventInvolvementRelationshipIds(data: EventMembershipData | null | undefined, eventId: string) {
  return EVENT_INVOLVEMENT_CONFIGS.flatMap((config) => (
    getMembershipIds(data, config.field).map((entityId) => (
      getEventInvolvementRelationshipId(eventId, config.entityType, entityId)
    ))
  ));
}

export function getEventInvolvementRelationshipChanges(
  eventId: string,
  previousData: EventMembershipData | null | undefined,
  nextData: EventMembershipData | null | undefined,
) {
  const relationshipsToCreate: Relationship[] = [];
  const relationshipIdsToDelete: string[] = [];

  EVENT_INVOLVEMENT_CONFIGS.forEach((config) => {
    const previousIds = new Set(getMembershipIds(previousData, config.field));
    const nextIds = new Set(getMembershipIds(nextData, config.field));

    nextIds.forEach((entityId) => {
      if (previousIds.has(entityId)) return;

      relationshipsToCreate.push({
        id: getEventInvolvementRelationshipId(eventId, config.entityType, entityId),
        entityAId: eventId,
        entityAType: 'events',
        entityBId: entityId,
        entityBType: config.entityType,
        forwardDescription: config.forwardDescription,
        reverseDescription: config.reverseDescription,
        managedBy: {
          type: 'event_involvement',
          eventId,
          entityType: config.entityType,
          entityId,
        },
      });
    });

    previousIds.forEach((entityId) => {
      if (nextIds.has(entityId)) return;
      relationshipIdsToDelete.push(
        getEventInvolvementRelationshipId(eventId, config.entityType, entityId),
      );
    });
  });

  return {
    relationshipsToCreate,
    relationshipIdsToDelete,
  };
}
